import Phaser from 'phaser';
import { Enemy, EnemyParams } from '../entities/Enemy';
import { Boss } from '../entities/Boss';
import { ENEMY_TYPES, getEnemyHp, BOSS_TYPE_CONFIG, getBossType } from '../config/enemies';
import { Biome, BIOMES, getBiomeForWave } from '../config/biomes';
import { audioManager } from '../audio/AudioManager';
import type { VFXSystem } from './VFXSystem';
import { t } from '../../i18n';

/** Time between waves in milliseconds. */
const WAVE_INTERVAL_MS = 5_000;

/** Minimum HP floor for boss add enemies. */
const MIN_ADD_HP = 5;
/** Speed bonus applied to boss add enemies over normal grunts. */
const ADD_SPEED_BONUS = 10;
/** How many px smaller add enemies are compared to grunts. */
const ADD_SIZE_REDUCTION = 4;
/** Baseline enemies per wave before scaling kicks in. */
const BASE_ENEMIES_PER_WAVE = 3;

/**
 * WaveManager — orchestrates endless waves of increasing difficulty.
 *
 * Rules:
 *  - First wave spawns 5 s after `start()` is called.
 *  - Wave count scales with a smoothed difficulty curve.
 *  - Enemy base HP uses staged scaling (early/mid/late).
 *  - Every 5th wave is a boss wave: one Boss + half the normal enemy count.
 *  - A "BOSS" warning banner is shown on boss waves.
 *  - The wave counter is always visible in the top-left corner.
 *  - Biome advances every WAVES_PER_BIOME waves; onBiomeChange is called.
 */
export class WaveManager {
  private readonly scene: Phaser.Scene;

  private currentWave: number = 0;
  private currentBiomeIndex: number = -1;
  private readonly enemies: Enemy[] = [];

  private waveTimer: Phaser.Time.TimerEvent | null = null;
  private waveText: Phaser.GameObjects.Text | null = null;

  /** Called whenever the active biome changes. */
  onBiomeChange?: (biome: Biome) => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Begin the wave cycle. Call once from the Game scene's `create()`. */
  start(): void {
    this.currentWave = 0;
    this.currentBiomeIndex = -1;

    this.waveText = this.scene.add
      .text(12, 8, `${t('game.wave')}: 0`, {
        fontSize: '18px',
        fontFamily: 'Arial Black, Arial',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setScrollFactor(0)
      .setDepth(100);

    this.scheduleNextWave();
  }

  // ─── Internal helpers ────────────────────────────────────────────────────

  private scheduleNextWave(): void {
    this.waveTimer = this.scene.time.addEvent({
      delay: WAVE_INTERVAL_MS,
      callback: this.spawnWave,
      callbackScope: this,
    });
  }

  private spawnWave(): void {
    this.currentWave++;
    this.waveText?.setText(`${t('game.wave')}: ${this.currentWave}`);

    // Wave announcement
    const vfx = this.scene.registry.get('vfx') as VFXSystem | undefined;
    vfx?.waveAnnouncement(this.currentWave);

    this.checkBiomeChange();

    const totalCount =
      BASE_ENEMIES_PER_WAVE +
      Math.floor(this.currentWave * 1.2) +
      Math.floor(this.currentWave / 4);
    const isBossWave = this.currentWave % 5 === 0;

    if (isBossWave) {
      this.spawnBoss();
      this.showBossWarning();
      // Spawn a reduced pack of regular enemies alongside the boss
      const regularCount = Math.max(1, Math.floor(totalCount / 2));
      for (let i = 0; i < regularCount; i++) {
        // Stagger spawns slightly so they don't all appear on the same pixel
        this.scene.time.addEvent({
          delay: i * 200,
          callback: this.spawnRegularEnemy,
          callbackScope: this,
        });
      }
    } else {
      for (let i = 0; i < totalCount; i++) {
        this.scene.time.addEvent({
          delay: i * 200,
          callback: this.spawnRegularEnemy,
          callbackScope: this,
        });
      }
    }

    this.scheduleNextWave();
  }

  /** Check whether the biome should advance and fire onBiomeChange if so. */
  private checkBiomeChange(): void {
    const newBiome = getBiomeForWave(this.currentWave);
    const newBiomeIndex = BIOMES.indexOf(newBiome);
    if (newBiomeIndex !== this.currentBiomeIndex) {
      this.currentBiomeIndex = newBiomeIndex;
      this.onBiomeChange?.(newBiome);
    }
  }

  /** Returns the enemy type pool for the current biome, or all types as fallback. */
  private getBiomeEnemyPool(): typeof ENEMY_TYPES {
    const biome = BIOMES[this.currentBiomeIndex >= 0 ? this.currentBiomeIndex : 0];
    const pool = ENEMY_TYPES.filter(e => biome.enemyTypes.includes(e.id));
    return pool.length > 0 ? pool : ENEMY_TYPES;
  }

  private spawnRegularEnemy(): void {
    const pos = this.randomEdgePosition();
    const pool = this.getBiomeEnemyPool();
    const config = pool[Math.floor(Math.random() * pool.length)];
    const params: EnemyParams = {
      hp: getEnemyHp(config.baseHp, this.currentWave),
      speed: config.speed,
      damage: config.damage,
      size: config.size,
      color: config.color,
    };
    this.enemies.push(new Enemy(this.scene, pos.x, pos.y, params));
  }

  private spawnBoss(): void {
    const pos = this.randomEdgePosition();
    // Use the grunt as the stat base for the boss calculation
    const base = ENEMY_TYPES[0];
    const baseParams: EnemyParams = {
      hp:     getEnemyHp(base.baseHp, this.currentWave),
      speed:  base.speed,
      damage: base.damage,
      size:   base.size,
      color:  base.color,
    };
    const boss = new Boss(
      this.scene,
      pos.x,
      pos.y,
      baseParams,
      this.currentWave,
      (ax, ay) => this.spawnAddEnemy(ax, ay),
    );
    this.enemies.push(boss);
    audioManager.play('bossEntrance');
  }

  /** Spawns a single add enemy at a specific position (used by Boss attack patterns). */
  private spawnAddEnemy(x: number, y: number): void {
    const config = ENEMY_TYPES[0]; // Use grunt stats for adds
    const params: EnemyParams = {
      hp:     Math.max(MIN_ADD_HP, getEnemyHp(config.baseHp, this.currentWave) >> 1),
      speed:  config.speed + ADD_SPEED_BONUS,
      damage: config.damage,
      size:   config.size - ADD_SIZE_REDUCTION,
      color:  0x00ff88,
    };
    this.enemies.push(new Enemy(this.scene, x, y, params));
  }

  private showBossWarning(): void {
    const vfx = this.scene.registry.get('vfx') as VFXSystem | undefined;
    const bossType = getBossType(this.currentWave);
    const bossName = t(`boss.${bossType}`) || BOSS_TYPE_CONFIG[bossType].name;

    if (vfx) {
      vfx.bossEntranceAnimation(bossName);
    } else {
      // Fallback: simple text warning
      const cam = this.scene.cameras.main;
      const text = this.scene.add
         .text(cam.width / 2, cam.height / 2, t('game.boss_warning'), {
          fontSize: '44px',
          fontFamily: 'Arial Black, Arial',
          color: '#ff0000',
          stroke: '#000000',
          strokeThickness: 8,
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(200);

      this.scene.tweens.add({
        targets: text,
        alpha: 0,
        y: cam.height / 2 - 80,
        duration: 2000,
        ease: 'Power2',
        onComplete: () => text.destroy(),
      });
    }
  }

  /**
   * Returns a random position just outside one of the four screen edges so
   * enemies always enter from the periphery.
   */
  private randomEdgePosition(): { x: number; y: number } {
    const { width, height } = this.scene.cameras.main;
    const margin = 32;
    const side = Math.floor(Math.random() * 4);
    switch (side) {
      case 0: return { x: Math.random() * width, y: -margin };          // top
      case 1: return { x: width + margin, y: Math.random() * height };  // right
      case 2: return { x: Math.random() * width, y: height + margin };  // bottom
      default: return { x: -margin, y: Math.random() * height };        // left
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /** All currently alive enemies (regular + bosses). */
  getEnemies(): Enemy[] {
    return this.enemies;
  }

  /**
   * Remove and destroy a specific enemy (call after it dies or when run ends).
   */
  removeEnemy(enemy: Enemy): void {
    const idx = this.enemies.indexOf(enemy);
    if (idx !== -1) this.enemies.splice(idx, 1);
    enemy.destroy();
  }

  /**
   * Move every enemy one step toward the player.
   * Call from the Game scene's `update(time, delta)`.
   */
  update(playerX: number, playerY: number, delta: number): void {
    for (const enemy of this.enemies) {
      enemy.update(playerX, playerY, delta);
    }
  }

  getCurrentWave(): number { return this.currentWave; }

  /** Returns the currently active biome, or the first biome if none started yet. */
  getCurrentBiome(): Biome {
    return BIOMES[this.currentBiomeIndex >= 0 ? this.currentBiomeIndex : 0];
  }

  /** Clean up all timers and game objects. */
  destroy(): void {
    this.waveTimer?.destroy();
    this.enemies.forEach(e => e.destroy());
    this.enemies.length = 0;
    this.waveText?.destroy();
  }
}
